import { foldCode, foldedRanges, unfoldCode } from '@codemirror/language';
import { EditorSelection } from '@codemirror/state';
import { EditorView } from '@codemirror/view';

import { devLog } from '../../../core/utils/logging.js';

/**
 * Sets the editor fold state to exactly the given positions.
 * Positions present in `prevFoldings` but absent from `foldings` are unfolded;
 * positions in `foldings` are folded (foldCode is idempotent for already-folded ranges).
 * @param foldings - target set of folding positions
 * @param editorView - Editor view
 * @param prevFoldings - previously active positions, used to compute which to unfold
 */
export function runFoldings(
  foldings: number[],
  editorView: EditorView,
  prevFoldings: number[] = [],
): void {
  const selection = editorView.state.selection;

  for (const pos of prevFoldings) {
    // Guard bounds only when we would unfold, not when pos is still in the active set.
    if (!foldings.includes(pos)) {
      if (editorView.state.doc.length <= pos) {
        devLog(
          `Editor unfolding position [${pos}] exceeds text length [${editorView.state.doc.length}]. Unfolding will be ignored.`,
        );
        continue;
      }
      // CodeMirror requires the cursor to be at the target position before unfold.
      editorView.dispatch(
        editorView.state.update({ selection: EditorSelection.cursor(pos) }),
      );
      unfoldCode(editorView);
    }
  }

  for (const foldPosition of foldings) {
    if (editorView.state.doc.length <= foldPosition) {
      devLog(
        `Editor folding position [${foldPosition}] exceeds text length [${editorView.state.doc.length}]. Folding will be ignored.`,
      );
      continue;
    }
    // CodeMirror requires the cursor to be at the target position before fold.
    editorView.dispatch(
      editorView.state.update({
        selection: EditorSelection.cursor(foldPosition),
      }),
    );
    foldCode(editorView);
  }

  // Snap cursor to the fold boundary if the saved position is now inside a folded range,
  // otherwise restoring it would cause CodeMirror to unfold the range to keep the cursor visible.
  const folded = foldedRanges(editorView.state);
  let targetPos = selection.main.head;
  const iter = folded.iter();
  while (iter.value !== null) {
    if (iter.from < targetPos && targetPos <= iter.to) {
      targetPos = iter.from;
      break;
    }
    iter.next();
  }
  editorView.dispatch(
    editorView.state.update({ selection: EditorSelection.cursor(targetPos) }),
  );
}
